import sellyApi from '../api/sellyApi';
import { getEnvVariables } from '../helpers/getEnvVariables';

function normalizeCurrencyCode(currency) {
  return String(currency || 'COP').toUpperCase();
}

const PIXEL_GUARD = '__cervalle_meta_pixel_snippet';

function readCookie(name) {
  if (typeof document === 'undefined') return '';
  const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : '';
}

export function getMetaFbp() {
  return readCookie('_fbp');
}

export function getMetaFbc() {
  return readCookie('_fbc');
}

function loadFbPixelBase() {
  if (typeof window === 'undefined' || window.fbq) return;
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
}

export function ensureMetaPixelInitialized() {
  if (typeof window === 'undefined' || window[PIXEL_GUARD]) return;
  const pixelId = getEnvVariables().META_PIXEL_ID;
  if (!pixelId) return;
  loadFbPixelBase();
  window.fbq('init', pixelId);
  window[PIXEL_GUARD] = true;
}

export function metaTrackPageView() {
  if (typeof window === 'undefined' || !window.fbq) return;
  window.fbq('track', 'PageView');
}

export function newMetaEventId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'cervalle_' + Date.now() + '_' + Math.random().toString(36).slice(2, 12);
}

export function metaPurchaseEventIdForOrder(transactionId) {
  return 'purchase-' + String(transactionId);
}

function buildCustomData(items, value, currency) {
  const norm = (items || []).map((row) => ({
    id: String(row.item_id),
    quantity: Number(row.quantity),
    item_price: Number(row.price),
  }));
  const content_ids = norm.map((c) => c.id);
  return {
    currency: normalizeCurrencyCode(currency),
    value: Number(value),
    content_ids,
    contents: norm,
    content_type: 'product',
  };
}

function forwardMetaCapiPublicEvent(eventName, eventId, customData) {
  const relativePath = getEnvVariables().META_CAPI_PUBLIC_EVENT_PATH;
  if (!relativePath || typeof window === 'undefined') return;
  const payload = {
    event_name: eventName,
    event_id: eventId,
    content_ids: customData.content_ids || [],
    value: Number(customData.value),
    currency: customData.currency,
    event_source_url: window.location.href,
    fbp: getMetaFbp() || undefined,
    fbc: getMetaFbc() || undefined,
  };
  sellyApi.post(relativePath.replace(/^\//, ''), payload).catch(() => {});
}

function fbqTrackWithDedup(eventName, customData, eventId) {
  if (typeof window === 'undefined' || !window.fbq) return;
  const options = eventId ? { eventID: eventId } : undefined;
  if (options) {
    window.fbq('track', eventName, customData, options);
  } else {
    window.fbq('track', eventName, customData);
  }
}

export function buildMetaOrderPayloadFields(metaEventIdInitiateCheckout) {
  const o = {
    metaFbp: getMetaFbp() || undefined,
    metaFbc: getMetaFbc() || undefined,
    metaEventSourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
  };
  if (metaEventIdInitiateCheckout) {
    o.metaEventIdInitiateCheckout = metaEventIdInitiateCheckout;
  }
  return o;
}

export function buildMetaAddPaymentPayloadFields(metaEventIdAddPaymentInfo) {
  const id = metaEventIdAddPaymentInfo || newMetaEventId();
  return {
    metaEventIdAddPaymentInfo: id,
    metaFbp: getMetaFbp() || undefined,
    metaFbc: getMetaFbc() || undefined,
    metaEventSourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
  };
}

export function trackMetaViewContent(currency, value, normalizedLineItems, eventId) {
  const pixelId = getEnvVariables().META_PIXEL_ID;
  if (!pixelId) return undefined;
  ensureMetaPixelInitialized();
  const id = eventId || newMetaEventId();
  const customData = buildCustomData(normalizedLineItems, value, currency);
  fbqTrackWithDedup('ViewContent', customData, id);
  forwardMetaCapiPublicEvent('ViewContent', id, customData);
  return id;
}

export function trackMetaAddToCart(currency, value, normalizedLineItems, eventId) {
  const pixelId = getEnvVariables().META_PIXEL_ID;
  if (!pixelId) return undefined;
  ensureMetaPixelInitialized();
  const id = eventId || newMetaEventId();
  const customData = buildCustomData(normalizedLineItems, value, currency);
  fbqTrackWithDedup('AddToCart', customData, id);
  forwardMetaCapiPublicEvent('AddToCart', id, customData);
  return id;
}

export function trackMetaInitiateCheckout(currency, value, normalizedLineItems, eventId) {
  const pixelId = getEnvVariables().META_PIXEL_ID;
  if (!pixelId) return undefined;
  ensureMetaPixelInitialized();
  const id = eventId || newMetaEventId();
  const customData = buildCustomData(normalizedLineItems, value, currency);
  fbqTrackWithDedup('InitiateCheckout', customData, id);
  return id;
}

export function trackMetaAddPaymentInfo(currency, value, normalizedLineItems, eventId) {
  const pixelId = getEnvVariables().META_PIXEL_ID;
  if (!pixelId) return undefined;
  ensureMetaPixelInitialized();
  const id = eventId || newMetaEventId();
  const customData = buildCustomData(normalizedLineItems, value, currency);
  fbqTrackWithDedup('AddPaymentInfo', customData, id);
  return id;
}

export function trackMetaPurchase(currency, value, normalizedLineItems, transactionId, eventId) {
  const pixelId = getEnvVariables().META_PIXEL_ID;
  if (!pixelId) return undefined;
  ensureMetaPixelInitialized();
  const id = eventId || metaPurchaseEventIdForOrder(transactionId);
  const customData = {
    ...buildCustomData(normalizedLineItems, value, currency),
    order_id: String(transactionId),
  };
  fbqTrackWithDedup('Purchase', customData, id);
  return id;
}
